/**
 * Parser for the ```andi-browser fenced block (contract C5, C7, C8).
 *
 * The browser guard's `browser_open` tool returns this block and the agent
 * streams it into chat verbatim, one SSE chunk. The chat renders it as a
 * live BrowserSessionCard. Anything that does not match the contract returns
 * null so MarkdownRenderer falls back to a CodeBlock, exactly like the
 * ```andi-report branch (budget/types.ts parseAndiReport).
 */

/** Contract C0/A8: `bs_` followed by uuid4().hex (32 lowercase hex characters). */
const SESSION_HANDLE_PATTERN = /^bs_[0-9a-f]{32}$/;

export interface AndiBrowserBlock {
  version: 1;
  sessionHandle: string;
  purpose: string;
  startUrl: string;
}

/**
 * Parse and shape guard an andi-browser payload. The handle is checked
 * strictly because it is interpolated into API paths and a WebSocket URL.
 */
export function parseAndiBrowser(raw: string): AndiBrowserBlock | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const obj = data as Record<string, unknown>;
  if (obj.version !== 1) return null;
  const handle = obj.session_handle;
  if (typeof handle !== 'string' || !SESSION_HANDLE_PATTERN.test(handle)) return null;
  if (typeof obj.purpose !== 'string' || typeof obj.start_url !== 'string') return null;
  return { version: 1, sessionHandle: handle, purpose: obj.purpose, startUrl: obj.start_url };
}

/** Hostname for display without a leading `www.`; '' when the text is not an absolute URL. */
export function displayDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** `m:ss`, or `h:mm:ss` from one hour on. Negative or non finite input reads as zero. */
export function formatElapsed(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = String(safe % 60).padStart(2, '0');
  if (hours > 0) {
    return `${String(hours)}:${String(minutes).padStart(2, '0')}:${seconds}`;
  }
  return `${String(minutes)}:${seconds}`;
}

/**
 * Widest cast frame the expanded/takeover view ever asks for. The guard
 * accepts up to 1920, but under gVisor Chrome only produces about 2 frames a
 * second at full HD (about 350 KB each); 1280 wide frames are well under half
 * the pixels, so they encode, relay and decode faster, which is what makes a
 * takeover feel responsive. Zoom past fit still upscales on a large screen.
 */
export const MAX_CAST_WIDTH = 1280;

/**
 * Requested cast frame width for the expanded/takeover view (guard accepts
 * 320..1920, `&max_width=`; this never asks for more than MAX_CAST_WIDTH).
 * Rounds to 160 px steps of device pixels so a phone does not pull wide
 * JPEGs it only ever downscales, the main source of takeover lag.
 */
export function computeCastWidth(innerWidth: number, devicePixelRatio: number): number {
  const dpr = devicePixelRatio || 1;
  return Math.min(MAX_CAST_WIDTH, Math.max(480, Math.round((innerWidth * dpr) / 160) * 160));
}

export interface CastViewportSize {
  width: number;
  height: number;
}

/** Below this CSS width the expanded view asks the guard to render a phone sized page instead of downscaling a desktop one. */
const PHONE_VIEWPORT_BREAKPOINT = 768;
/** The expanded dialog's own mobile gutter (p-4 both sides, `max-sm:` layout in BrowserSessionCard). */
const DIALOG_MOBILE_GUTTER = 32;
const VIEWPORT_WIDTH_MIN = 280;
const VIEWPORT_WIDTH_MAX = 1024;
const VIEWPORT_HEIGHT_MIN = 400;
const VIEWPORT_HEIGHT_MAX = 2000;

function clampViewport(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * True when the platform reports a coarse (touch) primary pointer. False,
 * never throwing, when `matchMedia` is unavailable (older browsers, SSR,
 * some test environments) rather than when there genuinely is no coarse
 * pointer — callers treat "unknown" the same as "not touch".
 */
function hasCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/**
 * True when the takeover view should ask for a phone sized remote page
 * rather than downscale a desktop one. Width alone would treat a narrow
 * desktop browser window as a phone, so this also requires a coarse
 * (touch) primary pointer; `Math.min(width, height)` rather than width
 * alone still catches a phone held in landscape, which is short rather
 * than narrow. `coarsePointer` defaults to a live `matchMedia` read so
 * production callers do not have to thread it through; tests pass it
 * explicitly to stay a pure check on the two sizes.
 */
export function isPhoneSizedViewer(
  innerWidth: number,
  innerHeight: number,
  coarsePointer: boolean = hasCoarsePointer()
): boolean {
  return Math.min(innerWidth, innerHeight) < PHONE_VIEWPORT_BREAKPOINT && coarsePointer;
}

/**
 * CSS viewport size to ask the guard's remote Chrome to render for a phone
 * sized takeover viewer (server contract: `viewport=WxH`, 280..1024 x
 * 400..2000; the server drops the parameter entirely if it is out of that
 * range rather than clamping it, so this function does the clamping before
 * the value is ever sent). The width mirrors the expanded dialog's own
 * mobile gutter, so the cast frame lands close to the canvas's actual
 * rendered width (near 1:1, not squeezed); the height covers most of the
 * screen, leaving room for the address bar and controls below the canvas.
 */
export function computeCastViewport(innerWidth: number, innerHeight: number): CastViewportSize {
  return {
    width: clampViewport(innerWidth - DIALOG_MOBILE_GUTTER, VIEWPORT_WIDTH_MIN, VIEWPORT_WIDTH_MAX),
    height: clampViewport(innerHeight * 0.7, VIEWPORT_HEIGHT_MIN, VIEWPORT_HEIGHT_MAX),
  };
}
